/**
 * Archivo: PaseComidas.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista para la gestión de los códigos del comedor. Permite a los usuarios con permisos adecuados
 * visualizar, añadir y modificar los códigos de comedor tanto personales como de invitados, así como descargarlos.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/CodigosServicios: Funciones para interactuar con el servicio de códigos del comedor.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera de la aplicación.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de la aplicación.
 * - Componentes/VentanaModificarCodigo: Componente para modificar un código existente.
 * - Componentes/VentanaAñadirCodigo: Componente para añadir un nuevo código.
 * 
 * Propósito:
 * Proveer una interfaz de usuario para la gestión de los códigos del comedor, incluyendo la capacidad de añadir,
 * modificar y descargar códigos personales y de invitados.
 */

import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import { obtenerCodigosComedor, descargarCodigo } from '../Servicios/CodigosServicios';
import VentanaModificarCodigo from '../Componentes/VentanaModificarCodigo';
import VentanaAñadirCodigo from '../Componentes/VentanaAñadirCodigo';

import downloadIcon from '../Imagenes/Iconos/DescargarBlanco.png';
import addIcon from '../Imagenes/Iconos/AñadirNegro.png';

const PaseComidas = () => {

  const urlCodigosComedor = `${process.env.REACT_APP_URL_IMAGENES}CodigosComedor/`
  const [codigoPersonal, setCodigoPersonal] = useState(null);
  const [codigoInvitados, setCodigoInvitados] = useState(null);
  const [token, setToken] = useState('');
  const [permisos, setPermisos] = useState(null);
  const [hayVentanas, setHayVentanas] = useState(false);
  const [mostrarModificarCodigo, setMostrarModificarCodigo] = useState(false);
  const [mostrarAñadirCodigo, setMostrarAñadirCodigo] = useState(false);
  const [tipoCodigo, setTipoCodigo] = useState('');
  const [codigoActual, setCodigoActual] = useState(null);

  const handleModificarCodigo = (codigo) => {
    setCodigoActual(codigo);
    setMostrarModificarCodigo(true);
    setHayVentanas(true);
  };

  const handleAñadirCodigo = (tipo) => {
    setTipoCodigo(tipo);
    setMostrarAñadirCodigo(true);
    setHayVentanas(true);
  };

  const closeModificarCodigo = () => {
    setMostrarModificarCodigo(false);
    setHayVentanas(false);
  };

  const closeAñadirCodigo = () => {
    setMostrarAñadirCodigo(false);
    setHayVentanas(false);
  };

  useEffect(() => {
    const obtenerDatos = async () => {
      const tokenNuevo = Cookies.get('token');
      const permisosNuevos = Cookies.get('permisos');
      setToken(tokenNuevo);
      setPermisos(permisosNuevos);
      await actualizarCodigosComedor(tokenNuevo);
    };
    obtenerDatos();
  }, [token]);

  const actualizarCodigosComedor = async (token) => {
    const resultado = await obtenerCodigosComedor(token);
    //console.log("Resultado: ", resultado)
    if (resultado.success) {
      const { codigoPersonal, codigoInvitado } = resultado.data;
      setCodigoPersonal(codigoPersonal);
      setCodigoInvitados(codigoInvitado);
      //console.log("Código personal:", codigoPersonal);
      //console.log("Código invitados:", codigoInvitado);
    } else {
      console.log(resultado ? resultado.message : 'Error desconocido');
    }
  };

  const handleDescargarCodigo = async (id_codigo) => {
    const token = Cookies.get('token');
    descargarCodigo(token, id_codigo)
    .then(response => {
      if (response.success) {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data], { type: 'image/jpeg' }));
          link.download = `imagen_${id_codigo}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      } else {
        console.log(response.message);
      }
  })
  .catch(() => {
      console.log('Error al descargar imagen');
  });
  };

  return (
    <>
      <MetaTags />
      <div className="fondo">
        <Cabecera permisos={permisos}/>
        <div className='d-none d-md-block '>
          <SegundaCabecera hayVentanas={hayVentanas} permisos={permisos} />
          {mostrarModificarCodigo && <VentanaModificarCodigo
            onClose={closeModificarCodigo}
            actualizarLista={async () => {
              await actualizarCodigosComedor(Cookies.get('token'));
            }}
            codigo={codigoActual}
          />}
          {mostrarAñadirCodigo && <VentanaAñadirCodigo
            onClose={closeAñadirCodigo}
            actualizarLista={async () => {
              await actualizarCodigosComedor(Cookies.get('token'));
            }}
            tipoCodigo={tipoCodigo}
          />}
          <div className='container-fluid d-flex p-2'>
            <div className='container-fluid col-11' style={{ opacity: hayVentanas ? 0.4 : 1 }}>
              <div className='row-fluid d-flex justify-content-between p-2'>
                <h3 className="text-black">Pase de comidas</h3>
              </div>
              <div className='container-fluid ' style={{ height: 'calc(100vh - 15rem)', overflowY: 'auto' }}>
                <div className='row mb-2'>
                  <div className='col-6'>
                    <h4>Código de comedor personal</h4>
                    <div className='codigo-container' style={{backgroundColor: 'black', height:'30vh'}}>
                      {codigoPersonal ? (
                        <>
                          <div className='row-fluid p-2 d-flex justify-content-between'>
                            <div>
                              <button className='btn' onClick={() => handleModificarCodigo(codigoPersonal)}>
                                Modificar
                              </button>
                            </div>
                            <div>
                              <img
                                src={downloadIcon}
                                alt="Descargar"
                                className='icono-descargar'
                                onClick={() => handleDescargarCodigo(codigoPersonal.id_codigo)}
                                style={{height:'3rem', cursor:'pointer'}}
                              />
                            </div>
                          </div>
                          <div className='container-fluid align-items-center d-flex justify-content-center' style={{height:'70%'}}>
                            <img 
                              src={urlCodigosComedor + codigoPersonal.imagen_codigo} 
                              alt="Código personal" 
                              className='codigo-imagen' 
                              style={{width:'25rem'}}
                            />
                          </div>
                        </>
                      ) : (
                        <div className='codigo-placeholder'>
                          <div className='container-fluid'>
                            <div className='row-fluid text-center'>
                              <span className='text-white customFontSizeTitulos'>Sube el código proporcionado por administración</span>
                            </div> 
                            <div className='container-fluid d-flex justify-content-center align-items-center mt-4'>
                              <button className='btn' onClick={() => handleAñadirCodigo('personal')}>
                                <img src={addIcon} alt="Añadir" className='icono-modificar' />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-6'>
                    <h4>Código de comedor invitados</h4>
                    <div className='codigo-container' style={{backgroundColor: 'black', height:'30vh'}}>
                      {codigoInvitados ? (
                        <>
                          <div className='row-fluid p-2 d-flex justify-content-between'>
                            <div>
                              <button className='btn' onClick={() => handleModificarCodigo(codigoInvitados)}>
                                Modificar
                              </button>
                            </div>
                            <div>
                              <img
                                src={downloadIcon}
                                alt="Descargar"
                                className='icono-descargar'
                                onClick={() => handleDescargarCodigo(codigoInvitados.id_codigo)}
                                style={{height:'3rem', cursor:'pointer'}}
                              />
                            </div>
                          </div>
                          <div className='container-fluid align-items-center d-flex justify-content-center' style={{height:'70%'}}>
                            <img 
                              src={urlCodigosComedor + codigoInvitados.imagen_codigo} 
                              alt="Código Invitados" 
                              className='codigo-imagen' 
                              style={{width:'25rem'}}
                            />
                          </div>
                        </>
                      ) : (
                        <div className='codigo-placeholder'>
                          <div className='container-fluid'>
                            <div className='row-fluid text-center'>
                              <span className='text-white customFontSizeTitulos'>Sube el código proporcionado por administración</span>
                            </div> 
                            <div className='container-fluid d-flex justify-content-center align-items-center mt-4'>
                              <button className='btn' onClick={() => handleAñadirCodigo('invitados')}>
                                <img src={addIcon} alt="Añadir" className='icono-modificar' />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='row mt-3'>
                  <div className='col-12'>
                    <span className='customFontSizeNormal text-black'>
                      Este código sirve para pasar el torno del comedor, es importante no saltarse el torno y, en caso de que no se detecte el código avisad a los cocineros, gracias.
                      Si no os funcionan los códigos reiteradamente, pasad por administración, gracias.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='d-block d-md-none '>
          {mostrarModificarCodigo && <VentanaModificarCodigo
            onClose={closeModificarCodigo}
            actualizarLista={async () => {
              await actualizarCodigosComedor(Cookies.get('token'));
            }}
            codigo={codigoActual}
          />}
          {mostrarAñadirCodigo && <VentanaAñadirCodigo
            onClose={closeAñadirCodigo}
            actualizarLista={async () => {
              await actualizarCodigosComedor(Cookies.get('token'));
            }}
            tipoCodigo={tipoCodigo}
          />}
          <div className='container-fluid d-flex p-0'>
            <div className='container-fluid col-12' style={{ opacity: hayVentanas ? 0.4 : 1 }}>
              <div className='row-fluid justify-content-between p-0 mt-2'>
                <h3 className="text-black">Pase de comidas</h3>
              </div>
              <div className='row mt-1 mb-2'>
                <div className='col-12'>
                  <span className='customFontSizeTextoMediano text-black'>
                    Este código sirve para pasar el torno del comedor, es importante no saltarse el torno y, en caso de que no se detecte el código avisad a los cocineros, gracias.
                    Si no os funcionan los códigos reiteradamente, pasad por administración, gracias.
                  </span>
                </div>
              </div>
              <div className='container-fluid' style={{ height: 'calc(100vh - 15rem)', overflowY: 'auto' }}>
                <div className='row mb-2'>
                  <div className='col-12 text-center'>
                    <div className='p-2'>
                      <span className='customFontSizeTextoGrande' style={{ fontWeight: 'bold' }}>Código de comedor personal</span>
                    </div>
                    <div className='codigo-container' style={{backgroundColor: 'black', height:'30vh'}}>
                      {codigoPersonal ? (
                        <>
                          <div className='row-fluid p-2 d-flex justify-content-between'>
                            <div>
                              <button className='btn' onClick={() => handleModificarCodigo(codigoPersonal)}>
                                Modificar
                              </button>
                            </div>
                            <div>
                              <img
                                src={downloadIcon}
                                alt="Descargar"
                                className='icono-descargar'
                                onClick={() => handleDescargarCodigo(codigoPersonal.id_codigo)}
                                style={{height:'3rem', cursor:'pointer'}}
                              />
                            </div>
                          </div>
                          <div className='container-fluid align-items-center d-flex justify-content-center' style={{height:'70%'}}>
                            <img 
                              src={urlCodigosComedor + codigoPersonal.imagen_codigo} 
                              alt="Código personal" 
                              className='codigo-imagen' 
                              style={{width:'25rem'}}
                            />
                          </div>
                        </>
                      ) : (
                        <div className='codigo-placeholder'>
                          <div className='container-fluid'>
                            <div className='row-fluid text-center'>
                              <span className='text-white customFontSizeTitulos'>Sube el código proporcionado por administración</span>
                            </div> 
                            <div className='container-fluid d-flex justify-content-center align-items-center mt-4'>
                              <button className='btn' onClick={() => handleAñadirCodigo('personal')}>
                                <img src={addIcon} alt="Añadir" className='icono-modificar' />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-12 text-center'>
                    <div className='p-2'>
                      <span className='customFontSizeTextoGrande' style={{ fontWeight: 'bold' }}>Código de comedor invitados</span>
                    </div>
                    <div className='codigo-container' style={{backgroundColor: 'black', height:'30vh'}}>
                      {codigoInvitados ? (
                        <>
                          <div className='row-fluid p-2 d-flex justify-content-between'>
                            <div>
                              <button className='btn' onClick={() => handleModificarCodigo(codigoInvitados)}>
                                Modificar
                              </button>
                            </div>
                            <div>
                              <img
                                src={downloadIcon}
                                alt="Descargar"
                                className='icono-descargar'
                                onClick={() => handleDescargarCodigo(codigoInvitados.id_codigo)}
                                style={{height:'3rem', cursor:'pointer'}}
                              />
                            </div>
                          </div>
                          <div className='container-fluid align-items-center d-flex justify-content-center' style={{height:'70%'}}>
                            <img 
                              src={urlCodigosComedor + codigoInvitados.imagen_codigo} 
                              alt="Código Invitados" 
                              className='codigo-imagen' 
                              style={{width:'25rem'}}
                            />
                          </div>
                        </>
                      ) : (
                        <div className='codigo-placeholder'>
                          <div className='container-fluid'>
                            <div className='row-fluid text-center'>
                              <span className='text-white customFontSizeTitulos'>Sube el código proporcionado por administración</span>
                            </div> 
                            <div className='container-fluid d-flex justify-content-center align-items-center mt-4'>
                              <button className='btn' onClick={() => handleAñadirCodigo('invitados')}>
                                <img src={addIcon} alt="Añadir" className='icono-modificar' />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PaseComidas;
